/**
 * Centralized keyframe animations for the Atlas International application.
 *
 * All reusable keyframe definitions should live here.
 * Import from this file instead of defining keyframes locally.
 */

import { keyframes } from "@emotion/react";

// ─── Fade Animations ─────────────────────────────────────────────────────────

/** Simple fade from opacity 0 to 1 */
export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/** Full 360-degree rotation (for spinners) */
export const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/**
 * Delayed visibility: fades from 0 to 1.
 * Commonly used with animation-delay for staggered reveals.
 * (Identical to fadeIn but kept as a semantic alias for nav-bar usage.)
 */
export const delayVisibility = fadeIn;

/**
 * Two-phase opacity: stays invisible for the first half,
 * then fades to full opacity in the second half.
 * Used for navigation bar items during expand/collapse transitions.
 */
export const inAndOutOpacity = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// ─── Header Dropdown Animation ───────────────────────────────────────────────

/** Expand from max-height 0 to large value with fade-in, for dropdown reveals */
export const expandWithFadeIn = keyframes`
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 9999px;
    opacity: 1;
  }
`;

// ─── Menu Slide Animations ───────────────────────────────────────────────────

export const slideOpenMenu = keyframes`
  from {
    width: 85px;
    padding: 0px;
  }
  to {
    width: 350px;
    padding: 0px 15px;
  }
`;

export const slideCloseMenu = keyframes`
  from {
    width: 350px;
    padding: 0px 15px;
  }
  to {
    width: 85px;
    padding: 0px;
  }
`;

// ─── Slide / Translate Animations ────────────────────────────────────────────

/** Slide in from the right and fade in (used by category labels) */
export const fadeAndSlideIn = keyframes`
  0% {
    transform: translate(300%, 0);
    opacity: 0;
  }
  70% {
    transform: translate(90%, 0);
    opacity: 0.7;
  }
  100% {
    transform: translate(110%, 0);
    opacity: 1;
  }
`;

/** Slide down from above and fade in (used by category labels) */
export const fadeAndSlideDown = keyframes`
  0% {
    transform: translate(0, -50%);
    opacity: 0;
  }
  70% {
    transform: translate(0, 20%);
    opacity: 0.7;
  }
  100% {
    transform: translate(0, 0);
    opacity: 1;
  }
`;

// ─── Spinner Animation ───────────────────────────────────────────────────────

/** Circle-spinner staggered opacity (used by GraphLoading) */
export const spinnerCircle = keyframes`
  0%,
  39%,
  100% {
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
`;

// fadeInAnimation is canonically exported from ./mixins (uses duration.base token)
